import { readChatStream } from './stream';
import { ProviderError, type ChatRequest, type ChatResult, type ChatTurn } from '../types';

interface GeminiPart {
  text?: string;
}

interface GeminiCandidate {
  content?: { role: string; parts?: GeminiPart[] };
  finishReason?: string;
}

interface GeminiResponse {
  candidates?: GeminiCandidate[];
  usageMetadata?: { promptTokenCount?: number; candidatesTokenCount?: number };
}

/**
 * Google Gemini via the Generative Language API (`:generateContent`).
 *
 * Two shape differences from the other providers, both handled here so callers
 * stay provider-agnostic:
 *   - the assistant role is called `model`, not `assistant`
 *   - system turns are not part of `contents` — they go in `systemInstruction`
 *
 * The key travels in the `x-goog-api-key` header rather than the `?key=` query
 * param the docs lead with, so it never lands in a proxy or access log.
 */
export async function chatGemini(
  request: ChatRequest,
  config: { apiKey: string; baseUrl: string },
): Promise<ChatResult> {
  const { messages, opts } = request;
  const { system, conversation } = splitSystem(messages, opts.systemPrompt);
  const model = opts.model ?? 'gemini-2.0-flash';

  const body = {
    contents: conversation.map((m) => ({
      role: m.role === 'assistant' ? 'model' : 'user',
      parts: [{ text: m.content }],
    })),
    ...(system ? { systemInstruction: { parts: [{ text: system }] } } : {}),
    generationConfig: {
      ...(opts.maxTokens === undefined ? {} : { maxOutputTokens: opts.maxTokens }),
      ...(opts.temperature === undefined ? {} : { temperature: opts.temperature }),
      ...(opts.topP === undefined ? {} : { topP: opts.topP }),
      ...(opts.stopSequences?.length ? { stopSequences: opts.stopSequences } : {}),
    },
  };

  // encodeURIComponent on the model only — the ':generateContent' suffix is
  // part of the method name, not the resource, so it must stay unescaped.
  const url = `${config.baseUrl}/v1beta/models/${encodeURIComponent(model)}:${opts.onDelta ? 'streamGenerateContent?alt=sse' : 'generateContent'}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-goog-api-key': config.apiKey,
    },
    body: JSON.stringify(body),
    signal: opts.signal,
    redirect: 'error', // don't follow 3xx to an unintended host
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ProviderError('gemini', res.status, text || res.statusText, text);
  }

  if (opts.onDelta && res.headers.get('content-type')?.includes('text/event-stream')) return readChatStream(res, 'gemini', opts.onDelta);

  const data = (await res.json()) as GeminiResponse;
  const text = (data.candidates?.[0]?.content?.parts ?? [])
    .map((p) => p.text ?? '')
    .filter(Boolean)
    .join('\n');
  const meta = data.usageMetadata;
  return {
    text,
    raw: data,
    ...(meta
      ? { usage: { inputTokens: meta.promptTokenCount ?? 0, outputTokens: meta.candidatesTokenCount ?? 0 } }
      : {}),
  };
}

function splitSystem(
  messages: ChatTurn[],
  extraSystem?: string,
): { system: string | undefined; conversation: ChatTurn[] } {
  const systems: string[] = [];
  const conversation: ChatTurn[] = [];
  for (const m of messages) {
    if (m.role === 'system') systems.push(m.content);
    else conversation.push(m);
  }
  if (extraSystem) systems.unshift(extraSystem);
  return { system: systems.length ? systems.join('\n\n') : undefined, conversation };
}
